import type { AssetsLibraryRef } from "@v2/features/assets/components/AssetsLibrary";
import type { Asset } from "schemas/generated/asset";

import React, { useMemo, useState } from "react";

import { useSemanticElement } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import AssetsLibrary from "@v2/features/assets/components/AssetsLibrary";
import { useChatFileDropZone } from "@v2/features/chat/hooks/useChatFileDropZone";
import { ChevronUp, Plus } from "lucide-react";

interface AssetModifierProps {
  onAssetChange: (assetSource: string | null) => void;
}

export const AssetModifier: React.FC<AssetModifierProps> = ({
  onAssetChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const assetsLibraryRef = React.useRef<AssetsLibraryRef>(null);
  const semanticElement = useSemanticElement();

  const elementType = semanticElement?.elementType ?? "image";

  // NOTE (Derek, 2026-02-23): Extract current asset URL from the semantic
  // element's outerHtml since agent-ui doesn't have v2's element designation.
  const assetUrl = useMemo(() => {
    if (!semanticElement?.outerHtml) {
      return null;
    }
    const container = document.createElement("div");
    container.innerHTML = semanticElement.outerHtml;
    const el = container.firstElementChild;
    if (!el) {
      return null;
    }
    return (
      el.getAttribute("src") ??
      el.querySelector("[src]")?.getAttribute("src") ??
      null
    );
  }, [semanticElement?.outerHtml]);

  const allowedAssetTypes: ("image" | "video")[] = (() => {
    if (elementType === "image") {
      return ["image"];
    }
    if (elementType === "video") {
      return ["video"];
    }
    return ["image", "video"];
  })();

  useChatFileDropZone({
    isActive: isOpen,
    acceptedTypes: allowedAssetTypes,
    onFileDrop: (files) => {
      assetsLibraryRef.current?.uploadFiles(files);
    },
  });

  const handleAssetSelect = (asset: Asset) => {
    onAssetChange(asset.url);
    setIsOpen(false);
  };

  const popoverContent = (
    <div
      className="flex w-full flex-col min-h-0 bg-white rounded-lg shadow-lg"
      style={{
        width: "var(--anchor-width)",
        height: "400px",
      }}
    >
      <AssetsLibrary
        ref={assetsLibraryRef}
        assetTypes={allowedAssetTypes}
        onAssetSelected={handleAssetSelect}
        shouldAllowActions={false}
        isDropDisabled
      />
    </div>
  );

  const assetSelector = (
    <div className="w-full">
      <div
        className="flex flex-row items-center justify-between p-2 gap-2 w-full h-12 bg-surface border-[0.5px] border-border rounded-lg cursor-pointer hover:bg-background/50 transition-colors"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-row items-center gap-2">
          <div className="box-content flex flex-row justify-center items-center w-8 h-8 bg-white border-[0.5px] border-dashed rounded-md overflow-hidden">
            {assetUrl ? (
              <AssetPreview assetUrl={assetUrl} elementType={elementType} />
            ) : (
              <Plus className="w-4 h-4 text-muted" />
            )}
          </div>
          <span className="text-xs font-medium text-muted">
            {assetUrl ? "Change Media" : "Add Media"}
          </span>
        </div>
        <ChevronUp
          className={`text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
          size={12}
        />
      </div>
    </div>
  );

  return (
    <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger render={<div className="w-full" />}>
        {assetSelector}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        showCloseButton={false}
        UNSAFE_className="bg-transparent border-0 p-0 shadow-none w-auto rounded-none gap-0"
      >
        {popoverContent}
      </PopoverContent>
    </PopoverRoot>
  );
};

const AssetPreview: React.FC<{
  assetUrl: string;
  elementType?: string;
}> = ({ assetUrl, elementType }) => {
  if (elementType === "video") {
    return (
      <video
        src={assetUrl}
        className="object-cover rounded-sm w-full h-full"
        preload="metadata"
        muted
      />
    );
  }
  return (
    <img
      src={assetUrl}
      alt="Selected media"
      className="object-cover rounded-sm w-full h-full"
    />
  );
};
